import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PadlockIcon, KeyIcon } from './EscapeIcons';
import { useLanguage } from '../context/LanguageContext';

export default function KeyLockPuzzle() {
  const [keyPos, setKeyPos] = useState(0);
  const [unlocked, setUnlocked] = useState(false);
  const [showPrize, setShowPrize] = useState(false);
  const trackRef = useRef(null);
  const dragging = useRef(false);
  const { t } = useLanguage();

  const handleDrag = (e) => {
    if (!dragging.current || !trackRef.current) return;
    const track = trackRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const pos = Math.max(0, Math.min(1, 1 - (clientX - track.left) / (track.width - 56)));
    setKeyPos(pos);

    if (pos > 0.85 && !unlocked) {
      setUnlocked(true);
      setShowPrize(true);
      dragging.current = false;
      setKeyPos(1);
    }
  };

  const handleStart = () => {
    if (!unlocked) dragging.current = true;
  };

  const handleEnd = () => {
    dragging.current = false;
    if (!unlocked && keyPos < 0.85) setKeyPos(0);
  };

  const sendToFacebook = () => {
    window.open('https://m.me/okiescape?text=I%20unlocked%20the%20lock%20puzzle%20on%20your%20website!%20Claiming%20my%20prize!', '_blank');
  };

  return (
    <section data-testid="key-lock-section" className="py-2 sm:py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-card border border-border rounded-2xl px-4 py-3"
        >
          {/* Lock and Key slider */}
          <div
            ref={trackRef}
            className="relative h-14 flex items-center select-none"
            onMouseMove={handleDrag}
            onMouseUp={handleEnd}
            onMouseLeave={handleEnd}
            onTouchMove={handleDrag}
            onTouchEnd={handleEnd}
          >
            {/* Lock on left */}
            <div className={`relative z-10 flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${unlocked ? 'bg-brand shadow-[0_0_20px_rgba(227,37,16,0.5)]' : 'bg-secondary border border-border'}`}>
              <PadlockIcon size={28} className={unlocked ? 'text-white' : 'text-muted-foreground'} />
            </div>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Draggable key on right */}
            <motion.div
              data-testid="key-slider"
              className={`relative z-20 flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center cursor-grab active:cursor-grabbing transition-shadow ${unlocked ? 'bg-brand shadow-[0_0_20px_rgba(227,37,16,0.5)]' : 'bg-brand/10 border border-brand/30 hover:border-brand hover:shadow-[0_0_15px_rgba(227,37,16,0.3)]'}`}
              style={{
                position: 'absolute',
                right: `${keyPos * (100 - 15)}%`,
              }}
              onMouseDown={handleStart}
              onTouchStart={handleStart}
              animate={unlocked ? { scale: [1, 1.2, 1] } : {}}
              transition={{ duration: 0.3 }}
            >
              <KeyIcon size={28} className={unlocked ? 'text-white' : 'text-brand'} />
            </motion.div>
          </div>

          {/* Unlocked prize message */}
          <AnimatePresence>
            {showPrize && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mt-8 text-center p-6 bg-brand/10 border border-brand/30 rounded-2xl"
              >
                <h3 className="font-heading text-3xl sm:text-4xl tracking-tight uppercase text-brand neon-text-glow mb-2">
                  {t('unlocked')}
                </h3>
                <p className="font-sans text-muted-foreground mb-6">
                  {t('unlockedMsg')}
                </p>
                <button
                  onClick={sendToFacebook}
                  data-testid="keylock-facebook-btn"
                  className="inline-block font-heading tracking-widest uppercase bg-brand text-white px-8 py-3 rounded-full hover:bg-brand-hover transition-colors neon-glow text-lg"
                >
                  {t('messageOnFacebook')}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
